import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { PickUriParams, PickUriService } from '@frontend/sports/navigation/core/feature/pick-uri';
import { PopupManager } from '@frontend/sports/popup/feature';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { UrlService } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { concatMap, filter, map, tap, withLatestFrom } from 'rxjs';

import { ShareMyBetApiService } from '../../../../lib/share-my-bet-api/share-my-bet-api.service';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { ExternalBetslipActions } from '../../../core/external-betslip-actions';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { PickType } from '../../../core/picks/pick-models';
import BetslipPickActions from '../../picks/actions';
import BetslipPickOptionsService from '../../share-my-bet/services/betslip-pick-options.service';
import { ISingleBetPickState } from '../../single-bet/state';
import { BetslipTypesActions } from '../../types/actions';
import { IBetslipTypeState } from '../../types/state';
import BetslipErrorsActions from '../../validation/actions';
import { IAffiliateLinkRequest } from '../affiliate.model';
import { AffiliateProcessorService } from '../services/affiliate-processor.service';
import AffiliateActions from './affiliate.actions';
import { affiliateStateSelector, betslipAffiliateSelector } from './affiliate.selectors';

@Injectable()
export default class AffiliateEffects {
    constructor(
        private store: Store<IBetslipRootState>,
        private pickUriService: PickUriService,
        private affiliateProcessorService: AffiliateProcessorService,
        private actions$: Actions,
        private betslipPickOptionsService: BetslipPickOptionsService,
        private shareMyBetApi: ShareMyBetApiService,
        private urlService: UrlService,
        private layoutService: AdaptiveLayoutService,
        private popupService: PopupManager,
        private userService: UserService,
        private userSettings: SyncUserSettingsService,
        private betslipConfig: BetslipConfig,
    ) {}

    handleGenerateAffiliateLinkEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AffiliateActions.generateAffiliateLink),
            concatLatestFrom(() => this.store.select(betslipAffiliateSelector)),
            map(([action, state]) => {
                const uriParams = this.getUriParams(action.payload, state.typeState, state.picks, state.typeState.base.currentSelectedType!);
                const options = this.betslipPickOptionsService.convertFromPicks(state.picks).filter(isDefined);

                const url = this.pickUriService.generate(options, uriParams);

                const isMixed = this.hasMixedTypes(state.picks);

                return AffiliateActions.setAffiliateLink({
                    link: {
                        url,
                        isMixed,
                    },
                });
            }),
        );
    });

    handleProcessAffiliateRequestEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.addAffiliatePicks),
            concatLatestFrom(() => this.store.select(betslipAffiliateSelector)),
            tap(([action, state]) => {
                if (state.picksCount + action.request.query.length < this.betslipConfig.maxBetslipBets) {
                    const betBuilderFixtureId = state.picks.filter((pick) => BetslipBetBuilderPick.isPick(pick)).map((pick) => pick.id.fixtureId);
                    const fixtureIds = state.picks.map((pick) => pick.id.fixtureId);
                    const existingFixtureIds = Object.values(Object.assign({}, fixtureIds));

                    this.affiliateProcessorService.processRequest(action.request, action.picksView, betBuilderFixtureId, existingFixtureIds);
                }
            }),
            filter(([action, state]) => state.picksCount + action.request.query.length > this.betslipConfig.maxBetslipBets),
            map(() => BetslipErrorsActions.showBetslipFullDialog()),
        );
    });

    handleAddAffiliatePickToBetslip$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AffiliateActions.addAffiliatePick),
            concatLatestFrom(() => this.store.select(betslipAffiliateSelector)),
            map(([action, state]) =>
                state.picks.some((pick) => pick.id.isEqual(action.payload.pickId))
                    ? BetslipPickActions.setSourceOfBet({
                          pickId: action.payload.pickId,
                          source: action.payload.tracking.source,
                          affiliate: action.payload.tracking.affiliate,
                      })
                    : BetslipPickActions.requestAddPick({
                          pickId: action.payload.pickId,
                          tracking: action.payload.tracking,
                          priceId: action.payload.priceId,
                          isPriceBoosted: action.payload.isPriceBoosted,
                          isBetBuilderEnabled: false,
                      }),
            ),
        );
    });

    handleAddAffiliateGroupPickToBetslip$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AffiliateActions.addAffiliateGroupPick),
            map((action) =>
                BetslipPickActions.requestAddGroupPick({
                    payload: action.picks,
                    headerTemplate: action.headerTemplate,
                }),
            ),
        );
    });

    handleAddAffiliateBetBuilderPickToBetslip$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AffiliateActions.addAffiliateBetBuilderPick),
            concatLatestFrom(() => this.store.select(betslipAffiliateSelector)),
            map(([action, state]) =>
                state.picks.some((pick) => pick.id.isEqual(action.pickId))
                    ? BetslipPickActions.setSourceOfBet({
                          pickId: action.pickId,
                          source: action.tracking.source,
                          affiliate: action.tracking.affiliate,
                      })
                    : BetslipPickActions.requestAddBetBuilderPick(action),
            ),
        );
    });

    handleAffiliatePicksErrorCheckEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipErrorsActions.picksErrorsCheckFinished),
            withLatestFrom(this.store.select(affiliateStateSelector)),
            filter(([_, affiliateState]) => affiliateState.addedPicks.length > 0),
            tap(([_, affiliateState]) => {
                if (!this.layoutService.current.hasRightColumn && !affiliateState.openedBetslip) {
                    this.popupService.open('betslip');
                }
            }),
            concatMap(function* ([_, affiliateState]) {
                yield BetslipPickActions.shareAffiliatePicks();

                if (affiliateState.optionRequest?.type) {
                    const inputBetslipType =
                        Object.values(BetslipType).find(
                            (currentType) => currentType.toLowerCase() === affiliateState.optionRequest?.type?.toLowerCase(),
                        ) ?? BetslipType.Single;

                    yield BetslipTypesActions.setSelectedType({
                        betslipType: inputBetslipType,

                        selectedByUser: true,

                        systemInfo: null,
                    });
                }

                if (affiliateState.optionRequest?.stake) {
                    yield BetslipTypesActions.setStake({
                        stake: affiliateState.optionRequest.stake,
                        stakeStr: affiliateState.optionRequest.stake.toString(),
                    });
                }

                yield AffiliateActions.openBetslip();
                yield AffiliateActions.resetAffiliateState();
            }),
        );
    });

    handleCompleteFailureOfAddingPicksToBetslip$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AffiliateActions.allPicksFailed),
            concatMap(() => [ExternalBetslipActions.shareBet(), AffiliateActions.resetAffiliateState()]),
        );
    });

    handleShareBet$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipPickActions.shareAffiliatePicks),
                concatLatestFrom(() => this.store.select(affiliateStateSelector)),
                tap(([_, state]) => {
                    // Excluding added picks that overlap with existing ones
                    const pickCount = state.addedPicks.length;
                    const uri = this.urlService.current();

                    if (pickCount || !state.pendingPicks.length) {
                        this.shareMyBetApi.handleShareBet(pickCount, uri);
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    private hasMixedTypes(picks: BetslipPick[]): boolean {
        return picks.some((p) => p.id.getPickType() === PickType.BetBuilderPick) && picks.some((p) => p.id.getPickType() !== PickType.BetBuilderPick);
    }

    private getUriParams(
        request: IAffiliateLinkRequest,
        typeState: IBetslipTypeState,
        picks: BetslipPick[],
        currentSelectedType: BetslipType,
    ): PickUriParams {
        const uriParams: PickUriParams = {};
        const pickType = currentSelectedType;

        if (request.addType && pickType) {
            uriParams.type = pickType.toString();
        }

        if (request.addStake) {
            uriParams.stake = this.getStake(typeState, this.userSettings.defaultStake) || this.userSettings.defaultStake || undefined;

            if (request.addCurrency) {
                uriParams.currency = this.userService.currency;
            }
        }

        if (request.source) {
            uriParams.source = request.source;
        }

        if (request.addPage) {
            uriParams.addPage = request.addPage;
        }

        return uriParams;
    }

    private getStake(typeState: IBetslipTypeState, defaultStake?: number | null): number | null {
        const { currentSelectedType } = typeState.base;
        switch (currentSelectedType) {
            case BetslipType.Single:
                const singlePickStakes = Object.values(typeState.singleBet.picks)
                    .map((pick: ISingleBetPickState) => pick.stake)
                    .filter((value, index, self) => self.indexOf(value) === index);

                return singlePickStakes && singlePickStakes.length === 1 ? singlePickStakes[0] : defaultStake || null;
            case BetslipType.Combo:
                return typeState.comboBet.stake;
            case BetslipType.System:
                return typeState.systemBet.stake;
            default:
                return null;
        }
    }
}
